import BaseModelHandler from './BaseModelHandler';
import Stock from '../models/Stock';
import portfolioDataCache from '../PortfolioDataCache';
import { toCountryCode } from '../../CountryMap';
import {
  MARKET_CHART_DAYS,
  normalizePortfolioChart,
  trimMarketData
} from '../../components/dashboard-views/marketDataUtils';

const PORTFOLIO_DATA_API = 'https://api.jashsoft.com/portfoliodata';
const COMPANY_CACHE_KEY = 'company';
const CHART_CACHE_KEY = 'chart';
const COMPANY_FETCH_MAX_RETRIES = 2;
const COMPANY_FETCH_RETRY_BASE_DELAY_MS = 1000;

class StockHandler extends BaseModelHandler {
  modelType = Stock;

  _fetchCompanyData(tickers) {
    const uniqueTickers = [...new Set(tickers.filter(Boolean))];
    if (uniqueTickers.length < 1) {
      return Promise.resolve({});
    }

    const cached = portfolioDataCache.get(COMPANY_CACHE_KEY);
    if (cached) {
      return Promise.resolve(cached);
    }

    const fetchCompany = (attempt = 0) => {
      return fetch(`${PORTFOLIO_DATA_API}/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        body: JSON.stringify(uniqueTickers)
      }).then((response) => {
        if (response.status === 504 && attempt < COMPANY_FETCH_MAX_RETRIES) {
          const delayMs = COMPANY_FETCH_RETRY_BASE_DELAY_MS * (2 ** attempt);
          return new Promise((resolve) => {
            setTimeout(resolve, delayMs);
          }).then(() => fetchCompany(attempt + 1));
        }
        return response.json();
      });
    };

    return fetchCompany().then((data) => {
      portfolioDataCache.set(COMPANY_CACHE_KEY, data);
      return data;
    }).catch((error) => {
      // Swallow error…
      return {};
    });
  }

  _applyCompanyData(ticker, data, stock) {
    stock.ticker = ticker;
    stock.sector = data.sector;
    stock.country = toCountryCode(data.country);
    stock.lastPrice = data.lastPrice;
    stock.regularMarketChange = data.regularMarketChange;
    stock.targetPrice = data.targetPrice;
    stock.beta = data.beta;
    stock.dividendYield = data.dividendYield;
    if (data.recommendationKey) {
      stock.recommendationKey = data.recommendationKey;
    }
    return stock;
  }

  read(modelInstance) {
    return this._fetchCompanyData([modelInstance.id]).then((parsedResponse) => {
      const ticker = modelInstance.id;
      const data = parsedResponse[ticker];
      if (data) {
        return this._applyCompanyData(ticker, data, modelInstance);
      }
      return modelInstance;
    });
  }

  fetchPrices(tickers) {
    const uniqueTickers = [...new Set(tickers.filter(Boolean))];

    return this._fetchCompanyData(uniqueTickers).then((companyData) => {
      uniqueTickers.forEach((ticker) => {
        let stock = Stock.find(ticker);
        if (!stock) {
          stock = new Stock({ id: ticker });
        }

        const company = companyData[ticker];
        if (company) {
          this._applyCompanyData(ticker, company, stock);
        }
      });
    });
  }

  fetchChart(transactions) {
    if (!transactions || transactions.length < 1) {
      return Promise.resolve({ dates: [], values: [] });
    }

    const cached = portfolioDataCache.get(CHART_CACHE_KEY);
    if (cached) {
      return Promise.resolve(cached);
    }

    return fetch(`${PORTFOLIO_DATA_API}/chart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      body: JSON.stringify(transactions)
    }).then((response) => {
      return response.json();
    }).then((data) => {
      const normalized = normalizePortfolioChart(data);
      portfolioDataCache.set(CHART_CACHE_KEY, normalized);
      return normalized;
    }).catch(() => {
      return { dates: [], values: [] };
    });
  }

  fetchBenchmark() {
    return fetch('https://api.jashsoft.com/marketdata/world').then((response) => {
      return response.json();
    }).then((parsedResponse) => {
      if (!parsedResponse) {
        return null;
      }
      const { dates, series } = trimMarketData({
        dates: parsedResponse.dates || [],
        series: parsedResponse.prices || [],
        days: MARKET_CHART_DAYS
      });
      return { dates, prices: series };
    }).catch(() => {
      return null;
    });
  }
}

export default StockHandler;
